/**
 * Stats Modal Component - Shows detailed statistics
 */
import React, { useState, useEffect } from 'react';
import { utilityAPI } from '../services/api';
import './StatsModal.css';

const StatsModal = ({ onClose }) => {
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    loadStats();
  }, []);

  const loadStats = async () => {
    try {
      setLoading(true);
      const response = await utilityAPI.getStats();
      setStats(response.data);
      setError(null);
    } catch (err) {
      console.error('Failed to load stats:', err);
      setError('Failed to load statistics');
    } finally {
      setLoading(false);
    }
  };

  const handleRefresh = () => {
    loadStats();
  };

  const handleClose = (e) => {
    if (e.target === e.currentTarget) {
      onClose();
    }
  };

  const handleEscape = (e) => {
    if (e.key === 'Escape') {
      onClose();
    }
  };

  if (loading) {
    return (
      <div className="modal-overlay" onClick={handleClose} onKeyDown={handleEscape}>
        <div className="modal-content stats-modal">
          <div className="modal-header">
            <h2>📊 Statistics</h2>
            <button className="btn-close" onClick={onClose}>×</button>
          </div>
          <div className="loading-state">Loading statistics...</div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="modal-overlay" onClick={handleClose} onKeyDown={handleEscape}>
        <div className="modal-content stats-modal">
          <div className="modal-header">
            <h2>📊 Statistics</h2>
            <button className="btn-close" onClick={onClose}>×</button>
          </div>
          <div className="error-state">{error}</div>
        </div>
      </div>
    );
  }

  return (
    <div className="modal-overlay" onClick={handleClose} onKeyDown={handleEscape}>
      <div className="modal-content stats-modal">
        <div className="modal-header">
          <h2>📊 Statistics</h2>
          <div className="modal-actions">
            <button className="btn-refresh" onClick={handleRefresh} title="Refresh">
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor">
                <path d="M23 4v6h-6M1 20v-6h6M20.49 15a9 9 0 1 1-2.12-9.36L23 10"/>
              </svg>
            </button>
            <button className="btn-close" onClick={onClose}>×</button>
          </div>
        </div>

        <div className="stats-grid">
          {/* Main Stats */}
          <div className="stat-card main-stat">
            <div className="stat-icon">🎵</div>
            <div className="stat-info">
              <div className="stat-value">{stats.total_songs}</div>
              <div className="stat-label">Total Songs</div>
            </div>
          </div>

          <div className="stat-card main-stat">
            <div className="stat-icon">⬇️</div>
            <div className="stat-info">
              <div className="stat-value">{stats.completed_downloads}</div>
              <div className="stat-label">Downloaded</div>
            </div>
          </div>

          <div className="stat-card main-stat">
            <div className="stat-icon">⏳</div>
            <div className="stat-info">
              <div className="stat-value">{stats.pending_downloads}</div>
              <div className="stat-label">Pending</div>
            </div>
          </div>

          <div className="stat-card main-stat">
            <div className="stat-icon">❤️</div>
            <div className="stat-info">
              <div className="stat-value">{stats.total_favorites}</div>
              <div className="stat-label">Favorites</div>
            </div>
          </div>

          {/* Progress Bar */}
          <div className="stat-card full-width">
            <div className="progress-label">
              <span>Download Progress</span>
              <span>{Math.round((stats.completed_downloads / stats.total_songs) * 100)}%</span>
            </div>
            <div className="progress-bar">
              <div
                className="progress-fill"
                style={{ width: `${(stats.completed_downloads / stats.total_songs) * 100}%` }}
              />
            </div>
          </div>

          {/* Additional Stats */}
          <div className="stat-card">
            <div className="stat-title">Library Status</div>
            <div className="stat-details">
              <div className="stat-row">
                <span>Completion Rate:</span>
                <span>{stats.total_songs > 0 ? Math.round((stats.completed_downloads / stats.total_songs) * 100) : 0}%</span>
              </div>
              <div className="stat-row">
                <span>Success Rate:</span>
                <span>{stats.total_songs > 0 ? Math.round((stats.completed_downloads / stats.total_songs) * 100) : 0}%</span>
              </div>
            </div>
          </div>

          <div className="stat-card">
            <div className="stat-title">Favorites Overview</div>
            <div className="stat-details">
              <div className="stat-row">
                <span>Favorited Songs:</span>
                <span>{stats.total_favorites}</span>
              </div>
              <div className="stat-row">
                <span>Of Downloaded:</span>
                <span>{stats.completed_downloads > 0 ? Math.round((stats.total_favorites / stats.completed_downloads) * 100) : 0}%</span>
              </div>
            </div>
          </div>
        </div>

        <div className="modal-footer">
          <button className="btn-primary" onClick={onClose}>Close</button>
        </div>
      </div>
    </div>
  );
};

export default StatsModal;